// @givmo/fundraisers-module — the `/org/{org_ref}/fundraisers/{fundraiser_id}/emails/*` operations a
// campaign's Emails tab calls (EMC-WEB1, over the backend's EMC-BE2/BE3/BE5 family, be#3854). The committed
// OpenAPI snapshot carries no `/emails` family, so these calls use the configured `givmoClient` escape
// hatch (`@givmo/crm`'s precedent, `api-client.md` § A whole ROUTE the snapshot lacks) and inherit the
// ordinary auth, App Check and error middleware. Every answer is decoded by `emails-decode.ts`.

import { givmoClient } from '@givmo/api-client';
import { PREVIEW_FIRST_NAME } from './email-designer-transport';

/** A spec as the server takes it (`EmailCampaignAudienceSpec`, version 1): the UNION of what it names. */
export interface EmailAudienceSpecBody {
  v: 1;
  segment_ids: string[];
  tags: string[];
  sources: string[];
  campaign_donors: boolean;
}

/** PATCH is absent-vs-null: only what is named changes (`EmailCampaignUpdateIn`). */
export interface EmailUpdateBody {
  audience_spec?: EmailAudienceSpecBody;
  audience_rule?: 'ask' | 'thank';
  scheduled_for?: string | null;
  tz?: string | null;
}

export interface EmailScheduleBody {
  scheduled_for: string;
  tz: string;
}

function base(orgRef: string, fundraiserId: string): string {
  return `/org/${encodeURIComponent(orgRef)}/fundraisers/${encodeURIComponent(fundraiserId)}/emails`;
}

function one(orgRef: string, fundraiserId: string, emailId: string): string {
  return `${base(orgRef, fundraiserId)}/${encodeURIComponent(emailId)}`;
}

/** The series: one page of the largest size is the whole series (a campaign holds at most 100). */
export async function fetchEmails(
  orgRef: string,
  fundraiserId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: base(orgRef, fundraiserId),
    query: { 'page[limit]': 100, 'page[offset]': 0 },
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** A new, empty draft (every field may be left for later), which the designer then writes. */
export async function createEmail(orgRef: string, fundraiserId: string): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: base(orgRef, fundraiserId),
    body: {},
    throwOnError: true,
  });
  return data;
}

/** Duplicate: a new draft copying the email's message, subject, preview text and audience, with no time, in
 *  `targetFundraiserId`, a campaign of the same organization; any email, one already out included (Jonathan,
 *  2026-11-01). */
export async function duplicateEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  targetFundraiserId: string,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${base(orgRef, fundraiserId)}/${encodeURIComponent(emailId)}/duplicate`,
    body: { target_fundraiser_id: targetFundraiserId },
    throwOnError: true,
  });
  return data;
}

/** Delete an email that isn't scheduled: a draft or a cancelled one (204). The server refuses a scheduled
 *  email (cancel it first) and one that went out, which stays in the series as the record. */
export async function deleteEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
): Promise<void> {
  await givmoClient.delete({ url: one(orgRef, fundraiserId, emailId), throwOnError: true });
}

export async function fetchEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: one(orgRef, fundraiserId, emailId),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * The email as a recipient gets it (the server's render), who it reaches, and the sender's standing. Its
 * greeting's name is the designer's merge-field marker, so this page draws the same "First name" chip
 * the designer does (`email-preview-stage.tsx`): one preview, never "Hi there," here and the chip there.
 */
export async function fetchEmailPreview(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${one(orgRef, fundraiserId, emailId)}/preview`,
    query: { first_name: PREVIEW_FIRST_NAME },
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** The segments and tags an audience can be picked from: names and counts only (D-EMC-10). */
export async function fetchAudienceOptions(
  orgRef: string,
  fundraiserId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${base(orgRef, fundraiserId)}/audience-options`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** EMC-BE8: what the campaign's emails that went out raised together (each email's own figures ride the email). */
export async function fetchEmailResultsSummary(
  orgRef: string,
  fundraiserId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${base(orgRef, fundraiserId)}/results`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** "About N people" for an audience not yet saved. */
export async function estimateAudience(
  orgRef: string,
  fundraiserId: string,
  spec: EmailAudienceSpecBody,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${base(orgRef, fundraiserId)}/audience-estimate`,
    body: { audience_spec: spec },
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function updateEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  body: EmailUpdateBody,
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: one(orgRef, fundraiserId, emailId),
    body,
    throwOnError: true,
  });
  return data;
}

export async function scheduleEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
  body: EmailScheduleBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${one(orgRef, fundraiserId, emailId)}/schedule`,
    body,
    throwOnError: true,
  });
  return data;
}

export async function cancelEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${one(orgRef, fundraiserId, emailId)}/cancel`,
    body: {},
    throwOnError: true,
  });
  return data;
}

/** One copy to the caller's own sign-in address; the server takes no address. */
export async function sendTestEmail(
  orgRef: string,
  fundraiserId: string,
  emailId: string,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${one(orgRef, fundraiserId, emailId)}/test`,
    body: {},
    throwOnError: true,
  });
  return data;
}
